/**
 * The user's own preferences: the Settings page's, and the durable presentation ones.
 *
 * WHAT "DURABLE PRESENTATION" MEANS, and why two of these are not Settings fields. `theme`
 * and `navCompact` are chosen from controls elsewhere in the app — the Appearance switch is
 * in Settings, the navigation's minimise control is on the sidebar itself — but both are the
 * same kind of thing: a choice about how the app looks that would be a broken feature if it
 * forgot itself on the next page. So both are persisted, by the same versioned-shape,
 * tolerant-read pattern, and both live here rather than in a store of their own. The
 * alternative was a `navStore` holding one boolean beside a `preferencesStore` holding
 * another, which is two homes for one idea.
 *
 * `defaultVocal`, `exportFormat` and `emailUpdates` are wired to real controls in
 * Settings — the "Default vocal style" selector, the "Export format" selector and
 * the "Email updates" toggle — and all three are preserved deliberately.
 *
 * NOTE for anyone doing dead-code cleanup: `defaultVocal` is read by Create when its
 * per-song Vocal choice is cleared. `exportFormat` and `emailUpdates` are written by
 * controls the user can see and operate; removing them would delete working UI even
 * before the backend stores them. `exportFormat` becomes the default in the Download
 * Center once real audio export exists (Phase 5), and `emailUpdates` becomes a
 * notification preference on the user record (Phase 3).
 *
 * MOCK: those two are NOT persisted. They reset on reload, and they are honest about
 * it because neither has anywhere to go yet.
 *
 * `theme` IS PERSISTED, and it is the exception for the same reason publisherStore is
 * one: Batch 6 §6 asks that the choice survive navigation and reloads, and a theme that
 * forgot itself on refresh would be a broken feature rather than a mock of one. It is
 * written to `localStorage` under `Lyrixa.theme`, following publisherStore's pattern —
 * versioned shape, a read that tolerates every way storage can fail, and a write that
 * swallows a refusal.
 *
 * It lives HERE rather than in a new store because it is a Settings preference and this
 * is the Settings preferences store. The brief asks for "the application's existing
 * settings/store architecture where appropriate", and a theme store next to a
 * preferences store would be two homes for one page's state.
 */
import { create } from 'zustand';

import { VOCALS } from '../data/music';
import { DARK, LIGHT } from '../styles/theme';

export type ExportFormat = 'MP3' | 'WAV';
export const NO_DEFAULT_VOCAL = 'None';

/**
 * The two themes.
 *
 * There is no `'system'` option, and that is a decision rather than an omission: the
 * brief asks for a switch between Dark and Light with dark as the default, and a third
 * mode that follows the OS would make "the default is dark" untrue for anyone whose
 * machine is set to light. Adding it later is a value in this union and a `matchMedia`
 * read in `applyTheme`, not a change to anything else.
 */
export type ThemeName = 'dark' | 'light';

/**
 * The default, stated once.
 *
 * DARK IS THE DEFAULT — §6 is explicit. Everything that can go wrong resolves to it:
 * no stored value, unreadable storage, a stored value that is not one of the two names,
 * and CSS with no `data-theme` attribute at all (see the `:root` block in global.css).
 */
export const DEFAULT_THEME: ThemeName = 'dark';

/** Where the theme choice lives. Mirrors `Lyrixa.publisher`'s naming. */
const STORAGE_KEY = 'Lyrixa.theme';

/**
 * Where the navigation width preference lives.
 *
 * A NEW KEY, AND NOT THE OLD ONE. There used to be a `Lyrixa.navCollapsed` key behind a
 * collapse button beside the brand mark; both were removed at the owner's request. This is
 * a different preference set by a different control, so it gets a different key and a
 * different shape — reusing the old name would hand this feature whatever a previous
 * install had stored for a feature that no longer exists, and would make the removal
 * reversible by accident. Anyone who still has `Lyrixa.navCollapsed` in their storage keeps
 * an ignored orphan; nothing in the app reads it.
 */
const NAV_STORAGE_KEY = 'Lyrixa.nav';

/** The user's default vocal choice, separate from the per-song Create selection. */
const DEFAULT_VOCAL_STORAGE_KEY = 'Lyrixa.defaultVocal';

/** What is written to storage. Versioned so a future shape change can migrate. */
interface PersistedShape {
  version: 1;
  theme: ThemeName;
}

/** The navigation preference's own shape, versioned for the same reason. */
interface PersistedNavShape {
  version: 1;
  compact: boolean;
}

interface PersistedDefaultVocalShape {
  version: 1;
  defaultVocal: string;
}

/** Whether a value read back from storage is a theme this app has. */
function isThemeName(value: unknown): value is ThemeName {
  return value === 'dark' || value === 'light';
}

/**
 * Reads the persisted theme, tolerating every way storage can fail.
 *
 * The try/catch is the same defence publisherStore documents — Safari private mode
 * throws on access, and a corrupted or half-written value throws on parse. The shape is
 * checked rather than trusted, because this value is user-editable from devtools and a
 * `theme` of `"blue"` would put an attribute on <html> that no CSS block matches,
 * leaving the app painted with dark tokens and no way to tell why.
 *
 * Every failure returns the default, which is dark.
 */
export function readPersistedTheme(): ThemeName {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULT_THEME;
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== 'object' || parsed === null) return DEFAULT_THEME;
    const shape = parsed as Partial<PersistedShape>;
    return isThemeName(shape.theme) ? shape.theme : DEFAULT_THEME;
  } catch {
    return DEFAULT_THEME;
  }
}

/** Writes the choice, ignoring a storage that refuses. */
function writePersistedTheme(theme: ThemeName): void {
  try {
    const shape: PersistedShape = { version: 1, theme };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(shape));
  } catch {
    // Quota exceeded, or storage disabled. The theme still applies for this session.
  }
}

/**
 * Reads whether the navigation was left minimised, tolerating every way storage can fail.
 *
 * The same defence `readPersistedTheme` documents, and every failure returns `false` — the
 * full sidebar. That is the right default for the same reason dark is the theme's: it is
 * what the app has always looked like, it is what someone who has never touched the control
 * expects, and a corrupted value must not be able to hide the navigation's labels on a
 * first visit.
 *
 * `typeof === 'boolean'` rather than a truthiness check, because this is user-editable from
 * devtools and `"false"` is a truthy string.
 */
export function readPersistedNavCompact(): boolean {
  try {
    const raw = localStorage.getItem(NAV_STORAGE_KEY);
    if (!raw) return false;
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== 'object' || parsed === null) return false;
    const shape = parsed as Partial<PersistedNavShape>;
    return typeof shape.compact === 'boolean' ? shape.compact : false;
  } catch {
    return false;
  }
}

/** Writes the navigation preference, ignoring a storage that refuses. */
function writePersistedNavCompact(compact: boolean): void {
  try {
    const shape: PersistedNavShape = { version: 1, compact };
    localStorage.setItem(NAV_STORAGE_KEY, JSON.stringify(shape));
  } catch {
    // Quota exceeded, or storage disabled. The choice still holds for this session.
  }
}

/** Reads a saved vocal default; new users and invalid values resolve to `None`. */
export function readPersistedDefaultVocal(): string {
  try {
    const raw = localStorage.getItem(DEFAULT_VOCAL_STORAGE_KEY);
    if (!raw) return NO_DEFAULT_VOCAL;
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== 'object' || parsed === null) return NO_DEFAULT_VOCAL;
    const shape = parsed as Partial<PersistedDefaultVocalShape>;
    const value = shape.defaultVocal;
    return value === NO_DEFAULT_VOCAL || (typeof value === 'string' && VOCALS.includes(value))
      ? value
      : NO_DEFAULT_VOCAL;
  } catch {
    return NO_DEFAULT_VOCAL;
  }
}

function writePersistedDefaultVocal(defaultVocal: string): void {
  try {
    const shape: PersistedDefaultVocalShape = { version: 1, defaultVocal };
    localStorage.setItem(DEFAULT_VOCAL_STORAGE_KEY, JSON.stringify(shape));
  } catch {
    // Storage may be unavailable. The selection still applies for this session.
  }
}

/**
 * Puts the theme on the document, which is the only thing that actually repaints anything.
 *
 * HOW THE THEME REACHES THE PIXELS. Every colour in the app resolves through the
 * `--lyx-*` custom properties — including the ~1250 inline `style={{ color: c.text2 }}`
 * props, because `c.text2` is the string `var(--lyx-text2)`. So the whole switch is this
 * attribute: `[data-theme='light']` in global.css re-declares the same fifteen properties
 * with light values and everything inheriting from them follows in one repaint. No
 * component subscribes to the theme and no component re-renders when it changes.
 *
 * THE ATTRIBUTE IS REMOVED FOR DARK rather than set to `"dark"`. Dark is the `:root`
 * block, so its absence is its presence — which means the document is already correct
 * before this function has ever run, and a JS failure cannot leave the app unpainted.
 *
 * Guarded for a non-DOM environment so importing this store from a node-environment test
 * cannot throw.
 */
export function applyTheme(theme: ThemeName): void {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  if (theme === 'light') root.setAttribute('data-theme', 'light');
  else root.removeAttribute('data-theme');

  /*
    The browser's own chrome — Android Chrome's address bar, the iOS status bar area.
    Not part of the page, so no custom property reaches it; it is a meta tag's content
    attribute and has to be written. The value is each theme's `--lyx-bg`, so the bar
    matches the page edge instead of staying charcoal above a white document.

    Optional by design: index.html ships the dark value, so if the tag were ever removed
    this quietly does nothing rather than throwing on a null.
  */
  const meta = document.querySelector('meta[name="theme-color"]');
  if (meta) meta.setAttribute('content', theme === 'light' ? LIGHT.bg : DARK.bg);
}

interface PreferencesState {
  /** Preferred download format. Purely a stored preference so far. */
  exportFormat: ExportFormat;
  /** Vocal style to use when Create's per-song vocal chip is cleared. */
  defaultVocal: string;
  /** Whether to receive product email updates. */
  emailUpdates: boolean;
  /** The active theme. Dark unless the user chose otherwise. */
  theme: ThemeName;

  /**
   * True when the user has minimised the navigation to its icon-only form.
   *
   * "COMPACT" RATHER THAN "COLLAPSED", and the name is doing work. A collapsed sidebar is
   * one that has gone away; this one is still there, still shows every destination, and
   * still marks the route you are on — it has changed size, not disappeared. The word also
   * keeps this field distinguishable at a glance from the removed `navCollapsed` it must
   * not be mistaken for.
   *
   * A PREFERENCE, WHICH IS WHY IT IS HERE AND NOT IN `uiStore`. That store's header draws
   * the line: it holds state several places act on and deliberately persists nothing —
   * `navDrawerOpen` is there because four things open and close the drawer, and it is
   * correctly forgotten between sessions. This is one intention expressed by one control
   * and meant to outlive the visit, which is what everything else in this file is.
   */
  navCompact: boolean;

  setExportFormat: (format: ExportFormat) => void;
  setDefaultVocal: (vocal: string) => void;
  setEmailUpdates: (enabled: boolean) => void;
  /** Switches theme, applies it to the document and remembers it. */
  setTheme: (theme: ThemeName) => void;
  /** Flips between the two, for the Settings switch. */
  toggleTheme: () => void;
  /** Minimises or restores the navigation, and remembers which. */
  setNavCompact: (compact: boolean) => void;
  /** Flips it, for the sidebar's minimise/restore control. */
  toggleNavCompact: () => void;
}

export const usePreferencesStore = create<PreferencesState>((set, get) => ({
  exportFormat: 'MP3',
  defaultVocal: readPersistedDefaultVocal(),
  emailUpdates: true,
  // Read once at import, like publisherStore's records. The matching `applyTheme` call
  // is in main.tsx: a store cannot touch the document as a side effect of being
  // imported, or importing it in a test would mutate that test's <html>.
  theme: readPersistedTheme(),
  // Read at import for the reason the theme is, and with an easier job: no document
  // attribute to apply, because this one is a React class on the <nav> and the first render
  // already has the right value.
  navCompact: readPersistedNavCompact(),

  setExportFormat(exportFormat) {
    set({ exportFormat });
  },
  setDefaultVocal(defaultVocal) {
    writePersistedDefaultVocal(defaultVocal);
    set({ defaultVocal });
  },
  setEmailUpdates(emailUpdates) {
    set({ emailUpdates });
  },

  setTheme(theme) {
    // Applied and persisted together with the state change, so there is no window in
    // which the store says light and the document is dark.
    applyTheme(theme);
    writePersistedTheme(theme);
    set({ theme });
  },

  toggleTheme() {
    get().setTheme(get().theme === 'dark' ? 'light' : 'dark');
  },

  setNavCompact(navCompact) {
    // Persisted with the state change, like setTheme, so there is no render in which the
    // sidebar is minimised and storage still says it is not.
    writePersistedNavCompact(navCompact);
    set({ navCompact });
  },

  toggleNavCompact() {
    get().setNavCompact(!get().navCompact);
  },
}));
